import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePermissions } from "@/lib/permissions";

export default function Tasks() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", assigned_to: "", assigned_to_name: "", due_date: "", priority: "medium", status: "pending" });

  const load = async () => {
    const [t, u] = await Promise.all([api.get("/tasks"), api.get("/users")]);
    setItems(t.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.title) return toast.error("Title required");
    await api.post("/tasks", f);
    toast.success("Task added"); setOpen(false); load();
    setF({ title: "", description: "", assigned_to: "", assigned_to_name: "", due_date: "", priority: "medium", status: "pending" });
  };

  const toggle = async (t) => {
    const s = t.status === "completed" ? "pending" : "completed";
    await api.put(`/tasks/${t.id}`, { status: s });
    load();
  };

  const grouped = { high: [], medium: [], low: [] };
  items.forEach(t => grouped[t.priority || "medium"].push(t));

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-muted-foreground mb-2">Workflow</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Tasks</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {can("customers.create") && (
            <DialogTrigger asChild><Button className="rounded-full" data-testid="add-task-button"><Plus size={16} className="mr-2" />New Task</Button></DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-xs">TITLE</Label><Input value={f.title} onChange={(e) => setF({...f,title:e.target.value})} data-testid="task-title" /></div>
              <div><Label className="text-xs">DESCRIPTION</Label><Textarea value={f.description} onChange={(e) => setF({...f,description:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">ASSIGN TO</Label>
                  <Select value={f.assigned_to} onValueChange={(v) => { const u = users.find(x => x.id === v); setF({...f,assigned_to:v,assigned_to_name:u?.name||""}); }}>
                    <SelectTrigger><SelectValue placeholder="Someone" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">DUE DATE</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({...f,due_date:e.target.value})} /></div>
                <div><Label className="text-xs">PRIORITY</Label>
                  <Select value={f.priority} onValueChange={(v) => setF({...f,priority:v})}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["high","medium","low"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-task-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["high","medium","low"].map(pr => (
          <Card key={pr} className="p-5">
            <div className="overline text-muted-foreground mb-4 capitalize">{pr} priority · {grouped[pr].length}</div>
            <div className="space-y-2">
              {grouped[pr].map(t => (
                <div key={t.id} className="flex items-start gap-3 p-3 rounded-md border border-border/40 hover:bg-secondary/30" data-testid={`task-${t.id}`}>
                  <Checkbox checked={t.status === "completed"} onCheckedChange={() => toggle(t)} data-testid={`task-check-${t.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${t.status==='completed'?'line-through text-muted-foreground':''}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                    <div className="text-xs text-muted-foreground mt-2 flex gap-3">
                      {t.assigned_to_name && <span>{t.assigned_to_name}</span>}
                      {t.due_date && <span>{t.due_date}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {!grouped[pr].length && <div className="text-xs text-muted-foreground text-center py-4">Nothing here</div>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
